import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Notification,
  type NotificationType,
  notificationTarget,
} from '@/api/adapters/notifications';
import { useNotificationTime } from '@/shared/hooks/use-notification-time';

/** Collapsed summary preview length (PRD §2.2 #5 / §4.2 — ~70자 요약 미리보기). */
const PREVIEW = 70;

/**
 * Does this body carry any of the tags §4.3 allows? (SHCT-667)
 *
 * Deliberately keyed on the allow-list rather than a generic `<...>` test: a plain-text notice that
 * happens to contain `a < b` must not be mistaken for markup and pushed down the innerHTML path.
 */
function looksLikeHtml(body: string): boolean {
  return /<(\/?)(b|strong|i|em|u|ul|ol|li|p|div|br)(\s[^>]*)?\/?>/i.test(body);
}

/**
 * HTML → plain text, for counting and previewing a `SYSTEM` notice body (PRD 08 §4.2, SHCT-667).
 *
 * Block-level tags become a space so `<p>첫 문단</p><p>둘째</p>` does not read as `첫 문단둘째`, then
 * the rest are dropped and entities decoded. Only used for the collapsed line and the length test —
 * the expanded view renders the original markup.
 */
function stripTags(html: string): string {
  const spaced = html.replace(/<\/(p|div|li|ul|ol)>|<br\s*\/?>/gi, ' ');
  const el = document.createElement('div');
  el.innerHTML = spaced.replace(/<[^>]*>/g, '');
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * A tag, anchored on a real tag name so ordinary prose survives: `<` has to be followed immediately by
 * a letter or `/`, which `a < b > c` never is.
 */
const STRAY_TAG = /<\/?[a-zA-Z][^<>]*>/;

/**
 * Drop any residual markup from a body that is NOT going down the innerHTML path, keeping its line
 * breaks (SHCT-667 수용 기준 ③ — 허용 목록 밖의 태그는 화면에 문자로 노출되지 않는다).
 *
 * <p>Needed because the server's notice allow-list and {@link looksLikeHtml} are not the same set. A
 * tag the server let through but this screen does not recognise — a `<span>` is the live case — makes
 * `looksLikeHtml` false, so the body reaches the text branch and JSX escapes it: the reader sees the
 * tag itself as characters. Stripping here means the reader gets the inner text either way.
 *
 * <p>Unlike {@link stripTags} this does NOT collapse whitespace. The text branch renders under
 * `white-space: pre-wrap`, so a plain notice's own line breaks are its paragraph structure and
 * flattening them here would lose it.
 */
function stripStrayTags(body: string): string {
  if (!STRAY_TAG.test(body)) return body;
  const el = document.createElement('div');
  el.innerHTML = body.replace(new RegExp(STRAY_TAG, 'g'), '');
  return el.textContent ?? '';
}

/** type → badge modifier class (colors defined in globals.css, per prototype TYPE_META). */
const BADGE_CLASS: Record<NotificationType, string> = {
  RISK: 'notif-badge--risk',
  HR_ABNORMAL: 'notif-badge--risk',
  PRO_DELAY: 'notif-badge--delay',
  SYSTEM: 'notif-badge--system',
};

const BADGE_LABEL_KEY: Record<NotificationType, string> = {
  RISK: 'notifications.type.risk',
  HR_ABNORMAL: 'notifications.type.hrAbnormal',
  PRO_DELAY: 'notifications.type.proDelay',
  SYSTEM: 'notifications.type.system',
};

interface NotificationItemProps {
  notification: Notification;
  /** Whether this SYSTEM card is currently expanded (single-open, owned by the page). */
  isExpanded: boolean;
  /** Invoked when the card is activated (click / Enter / Space). The page decides
   *  what activation does (mark read + navigate, or toggle expand). */
  onActivate: () => void;
}

export function NotificationItem({
  notification,
  isExpanded,
  onActivate,
}: NotificationItemProps) {
  const { t } = useTranslation('medical');
  const formatTime = useNotificationTime();

  const isUnread = notification.status === 'UNREAD';
  const isNavigable = notificationTarget(notification) !== null;
  const body = notification.body ?? '';
  // SHCT-667 — 의료진웹 PRD 08 §4.2 render contract: `SYSTEM`(공지) carries the admin's rich-text
  // HTML and is drawn as markup; `RISK`·`HR_ABNORMAL`·`PRO_DELAY` are backend-generated plain text
  // and keep pre-wrap. Sanitising is the admin API's job at SAVE time (어드민 PRD 13 §3.2 · §4.3,
  // A안 확정 2026-08-25 / SHCT-833), so this screen renders what it receives and does not re-filter.
  // Only a body that actually carries markup is rendered as markup.
  //
  // The type alone is not enough: the body has to look like HTML too. medical-api now passes a notice
  // body through as stored and strips only patient-event FEED rows, but a notice whose author never
  // applied any formatting still arrives as bare text with real line breaks in it. Rendering that
  // through innerHTML with `white-space: normal` would COLLAPSE those breaks — the notice would lose
  // its paragraphs and read worse than before it was ever rendered as markup.
  const isHtmlBody = notification.type === 'SYSTEM' && looksLikeHtml(body);
  // What the reader sees when the body is NOT rendered as markup. A body that fails `looksLikeHtml`
  // may still carry a tag the server allowed, and printing it as text puts that tag on screen as
  // characters — the SHCT-667 QC finding. Stripping keeps the inner text and the line breaks.
  const safeText = isHtmlBody ? body : stripStrayTags(body);
  // The 70자 preview counts PLAIN TEXT — tags stripped first (§4.2), or a single <p> wrapper would
  // eat most of the budget and every notice would look "long".
  const plainBody = isHtmlBody ? stripTags(body) : safeText;
  const isLong = plainBody.length > PREVIEW;

  // Actionable = navigates, OR is a long expandable notice, OR is unread (a click marks it read).
  const isActionable = isNavigable || isLong || isUnread;

  // PRD §3.2 / prototype: collapsed = whitespace-collapsed preview + …; expanded = full body,
  // original line breaks preserved (pre-wrap).
  const collapsedPreview = isLong
    ? `${plainBody.slice(0, PREVIEW).replace(/\s+/g, ' ').trim()}…`
    : plainBody;
  const showFull = isExpanded && isLong;
  // The whole body is on screen in TWO cases, not one: the card is expanded, or the notice was never
  // long enough to collapse in the first place — UAT MW-08-032, 「70자 이하 알림은 본문 전문이 그대로
  // 표시된다」. Keying the markup render on `showFull` alone left every short notice as plain text with
  // its tags stripped, so a one-line 굵게 공지 — the shape an operator is most likely to write — showed
  // no formatting at all and looked like the defect SHCT-667 set out to remove.
  const showsWholeBody = showFull || !isLong;
  // Collapsed is always plain text — the preview is a summary line, not a rendering surface, and a
  // sliced HTML string would cut a tag in half.
  const shownBody = showsWholeBody ? safeText : collapsedPreview;
  const renderAsHtml = showsWholeBody && isHtmlBody;

  // Meta hint (PRD §2.2 #5 / prototype): navigable → 확인, long notice → 펼치기/접기, else none.
  let hint = '';
  if (isNavigable) hint = ` · ${t('notifications.hint.goReview')}`;
  else if (isLong)
    hint = isExpanded
      ? ` · ${t('notifications.hint.collapse')}`
      : ` · ${t('notifications.hint.expand')}`;

  const handleKeyDown = (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };

  return (
    <li
      className={`notif-item ${isUnread ? 'notif-item--unread' : 'notif-item--read'}`}
      onClick={isActionable ? onActivate : undefined}
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      onKeyDown={isActionable ? handleKeyDown : undefined}
    >
      <span className="notif-item__dot" aria-hidden={!isUnread} />
      <div className="notif-item__body">
        <div className="notif-item__title">
          <span className={`notif-badge ${BADGE_CLASS[notification.type]}`}>
            {t(BADGE_LABEL_KEY[notification.type])}
          </span>
          <span className="notif-item__title-text">{notification.title}</span>
        </div>
        <div className="notif-item__meta">
          {formatTime(notification.createdAt)}
          {hint}
        </div>
        {renderAsHtml ? (
          <div
            className={`notif-item__desc notif-item__desc--expanded notif-item__desc--html`}
            // Server-sanitised at save time against the §4.3 allow-list
            // (`b strong i em u ul ol li p div br`, attributes stripped). PRD 13 v1.7 fixes the
            // sanitising point at the admin API and states that receiving channels do not filter again.
            dangerouslySetInnerHTML={{ __html: shownBody }}
          />
        ) : (
          <div
            className={`notif-item__desc ${showFull ? 'notif-item__desc--expanded' : ''}`}
          >
            {shownBody}
          </div>
        )}
      </div>
    </li>
  );
}
